import { COMPONENT_META, COMPONENT_NAMES, type ComponentName } from "@minne/core";

/**
 * What the editor's "Insert" menu drops at the cursor: the `template` carried by each
 * component's metadata in @minne/core, so the menu cannot drift from the guide or the
 * schemas (the templates are validated in core's guide tests). `select` is the text to
 * highlight afterwards so the first thing to change is already selected.
 */
export interface ComponentTemplate {
  name: ComponentName;
  label: string;
  hint: string;
  snippet: string;
  select: string;
}

export const COMPONENT_TEMPLATES: ComponentTemplate[] = COMPONENT_NAMES.map((name) => {
  const meta = COMPONENT_META[name];
  return { name, label: meta.title, hint: meta.summary, snippet: meta.template.snippet, select: meta.template.select };
});

/**
 * Insert a snippet at the textarea's cursor as its own paragraph: blank lines are
 * added around it as needed. Returns the new text and the selection to apply
 * (the template's `select` text if present, else the end of the snippet).
 */
export function insertSnippet(
  text: string,
  selectionStart: number,
  selectionEnd: number,
  template: Pick<ComponentTemplate, "snippet" | "select">,
): { text: string; selectionStart: number; selectionEnd: number } {
  const before = text.slice(0, selectionStart);
  const after = text.slice(selectionEnd);
  const lead = before.length === 0 ? "" : before.endsWith("\n\n") ? "" : before.endsWith("\n") ? "\n" : "\n\n";
  const trail = after.length === 0 ? "\n" : after.startsWith("\n\n") ? "" : after.startsWith("\n") ? "\n" : "\n\n";
  const inserted = `${lead}${template.snippet}${trail}`;
  const snippetStart = before.length + lead.length;
  const idx = template.select ? template.snippet.indexOf(template.select) : -1;
  const selStart = idx >= 0 ? snippetStart + idx : snippetStart + template.snippet.length;
  const selEnd = idx >= 0 ? selStart + template.select.length : selStart;
  return { text: before + inserted + after, selectionStart: selStart, selectionEnd: selEnd };
}
